/// <reference path="./global.d.ts" />
import { useEffect, useRef, useCallback } from 'react';
import { useRouter } from 'next/router';
import { getChatbotConfig } from './config';
import { CHATBOT_PARAMS_SYNC, type ChatbotParamsSyncDetail, type ChatbotEnv } from './types';

export interface UseChatbotOptions {
  theme: 'dark' | 'light';
  env: ChatbotEnv;
  pageType?: 'trade' | 'default';
  token?: string;
  uid?: string;
}

function isSdkReady(): boolean {
  return typeof window !== 'undefined' && 'ChatBot' in window;
}

function waitForSdk(): Promise<void> {
  return new Promise((resolve) => {
    if (isSdkReady()) {
      resolve();
      return;
    }
    const timer = setInterval(() => {
      if (isSdkReady()) {
        clearInterval(timer);
        resolve();
      }
    }, 300);
  });
}

export function useChatbot(options: UseChatbotOptions) {
  const { theme, env, pageType = 'default', token, uid } = options;
  const router = useRouter();
  const locale = router.locale ?? 'en';
  const initRef = useRef(false);

  const paramsRef = useRef({
    theme,
    locale,
    pageType,
    token: token || '',
    uid,
  });
  paramsRef.current = {
    theme,
    locale,
    pageType,
    token: token || '',
    uid,
  };

  const envRef = useRef(env);
  envRef.current = env;

  const applyChatbotUpdate = useCallback(() => {
    if (!initRef.current || !isSdkReady()) return;
    const { theme, locale, pageType, token, uid } = paramsRef.current;
    const config = getChatbotConfig(envRef.current, { locale, theme, pageType, token, uid });
    window.ChatBot.update({
      lang: config.lang,
      theme: config.theme,
      pageType: config.pageType,
      token: token || '',
      cfIpCountry: config.cfIpCountry,
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    waitForSdk().then(() => {
      if (initRef.current) return;
      initRef.current = true;

      const snap = paramsRef.current;
      const config = getChatbotConfig(envRef.current, snap);
      window.ChatBot.init(config);

      window.ChatBot.on('chatbot:login-required', () => {
        const locale = paramsRef.current.locale ?? 'en';
        window.location.href = `/${locale}/login`;
      });

      window.ChatBot.on('chatbot:navigate', (detail) => {
        if (detail?.url) {
          if (detail.newTab) {
            window.open(detail.url, '_blank');
          } else {
            // 直接跳转绝对 URL，避免 router.push 在有 basePath 的应用（如 activity）
            // 中自动拼接 basePath 导致路径错误（如 /activity/en/support/FAQ）
            window.location.href = detail.url;
          }
        }
      });

      const latest = paramsRef.current;
      const latestConfig = getChatbotConfig(envRef.current, latest);
      window.ChatBot.update({
        lang: latestConfig.lang,
        theme: latestConfig.theme,
        pageType: latestConfig.pageType,
        token: latest.token || '',
        cfIpCountry: latestConfig.cfIpCountry,
      });
    });

    return () => {
      if (initRef.current && isSdkReady()) {
        window.ChatBot.destroy();
        initRef.current = false;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    applyChatbotUpdate();
  }, [locale, pageType, token, uid, applyChatbotUpdate]);

  useEffect(() => {
    const onHeaderSync = (event: Event) => {
      const detail = (event as CustomEvent<ChatbotParamsSyncDetail>).detail;
      if (!detail || !initRef.current || !isSdkReady()) return;
      const base = paramsRef.current;
      const effectiveLocale = detail.locale ?? base.locale;
      const effectiveTheme = detail.theme ?? base.theme;
      paramsRef.current = { ...base, theme: effectiveTheme, locale: effectiveLocale };
      const config = getChatbotConfig(envRef.current, {
        locale: effectiveLocale,
        theme: effectiveTheme,
        pageType: base.pageType,
        token: base.token,
        uid: base.uid,
      });
      window.ChatBot.update({
        lang: config.lang,
        theme: config.theme,
        pageType: config.pageType,
        token: base.token || '',
        cfIpCountry: config.cfIpCountry,
      });
    };
    window.addEventListener(CHATBOT_PARAMS_SYNC, onHeaderSync);
    return () => window.removeEventListener(CHATBOT_PARAMS_SYNC, onHeaderSync);
  }, []);

  const openChatbot = useCallback(() => {
    if (isSdkReady()) {
      window.ChatBot.open();
    }
  }, []);

  const closeChatbot = useCallback(() => {
    if (isSdkReady()) {
      window.ChatBot.close();
    }
  }, []);

  return { openChatbot, closeChatbot };
}
